import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, DollarSign, Lightbulb, Target } from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";
import { BrandTextarea } from "@/components/brand/brand-textarea";
import {
	AGENT_CATEGORIES,
	type AgentCategory,
	CATEGORY_ICONS,
	getAgentCategoryLabel,
	normalizeAgentCategory,
} from "@/lib/agent-categories";
import { toast } from "@/lib/toast";
import type { AgentInsights as AgentInsightsData } from "@/routes/api/agents/generate-insights";

interface AgentInsightsProps {
	agentId: string;
	agentInsights: string | null | undefined;
}

const INSIGHT_SKELETON_KEYS = [
	"value",
	"audience",
	"problem",
	"details",
	"pricing",
] as const;

const EMPTY_INSIGHTS: AgentInsightsData = {
	valueProposition: "",
	problemSolved: "",
	pricing: "",
	targetAudience: "",
	categories: [],
};

async function fetchInsights(
	agentId: string,
	force = false,
): Promise<AgentInsightsData> {
	const res = await fetch("/api/agents/generate-insights", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ agentId, force }),
	});
	if (res.status === 429) {
		const data = (await res.json()) as {
			error: string;
			retryAfterSeconds?: number;
		};
		throw Object.assign(new Error(data.error), {
			status: 429,
			retryAfterSeconds: data.retryAfterSeconds,
		});
	}
	if (!res.ok) {
		const data = (await res.json().catch(() => null)) as {
			error?: string;
		} | null;
		throw new Error(data?.error || "Failed to generate insights");
	}
	const data = (await res.json()) as { insights: AgentInsightsData };
	return data.insights;
}

async function saveInsights(
	agentId: string,
	insights: AgentInsightsData,
): Promise<AgentInsightsData> {
	const res = await fetch("/api/agents/generate-insights", {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ agentId, insights }),
	});
	if (!res.ok) throw new Error("Failed to save insights");
	const data = (await res.json()) as { insights: AgentInsightsData };
	return data.insights;
}

export function AgentInsights({ agentInsights }: AgentInsightsProps) {
	const parsed = useMemo<AgentInsightsData | null>(() => {
		if (!agentInsights) return null;
		try {
			return JSON.parse(agentInsights) as AgentInsightsData;
		} catch {
			return null;
		}
	}, [agentInsights]);

	const insights = parsed;

	if (!insights) return null;

	return (
		<section className="mt-14">
			<div className="pb-6">
				<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
					&gt; Agent insights
				</span>
			</div>
			<div className="border-t border-border pt-5">
				{insights ? (
					<div className="grid gap-5 sm:grid-cols-2">
						<InsightRow
							icon={<Lightbulb className="size-3.5" />}
							label="Value proposition"
							value={insights.valueProposition}
						/>
						<InsightRow
							icon={<Target className="size-3.5" />}
							label="Target audience"
							value={insights.targetAudience}
						/>
						<InsightRow
							icon={<Target className="size-3.5" />}
							label="Problem solved"
							value={insights.problemSolved}
						/>
						{(insights.categories ?? []).length > 0 ? (
							<InsightRow
								icon={<Building2 className="size-3.5" />}
								label="Market"
								value={
									<div className="flex flex-wrap gap-1.5">
										{(insights.categories ?? []).map((cat) => {
											const Icon = CATEGORY_ICONS[cat] ?? Building2;
											return (
												<span
													key={cat}
													className="inline-flex items-center gap-1.5 border border-border bg-background px-2 py-0.5 font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground"
												>
													<Icon className="size-3 shrink-0" />
													{getAgentCategoryLabel(cat)}
												</span>
											);
										})}
									</div>
								}
							/>
						) : null}
						<InsightRow
							icon={<DollarSign className="size-3.5" />}
							label="Pricing"
							value={insights.pricing}
						/>
					</div>
				) : null}
			</div>
		</section>
	);
}

export function AgentInsightsEditor({ agentId }: { agentId: string }) {
	const queryClient = useQueryClient();
	const {
		data: fetched,
		error: fetchError,
		isLoading,
	} = useQuery({
		queryKey: ["agent-insights", agentId],
		queryFn: () => fetchInsights(agentId, false),
		retry: false,
	});

	const [form, setForm] = useState<AgentInsightsData>(EMPTY_INSIGHTS);
	const formId = useId();

	useEffect(() => {
		if (fetched) {
			setForm(fetched);
			queryClient.invalidateQueries({ queryKey: ["public-agent"] });
		} else {
			setForm(EMPTY_INSIGHTS);
		}
	}, [fetched, queryClient]);

	const toggleCategory = (cat: AgentCategory) =>
		setForm((f) => {
			if (f.categories.includes(cat)) {
				return { ...f, categories: f.categories.filter((c) => c !== cat) };
			}
			if (f.categories.length >= 3) return f;
			return { ...f, categories: [...f.categories, cat] };
		});

	const [regenCoolingDown, setRegenCoolingDown] = useState(false);
	const invalidateAgentProfile = () =>
		queryClient.invalidateQueries({ queryKey: ["public-agent"] });

	const regenerateMutation = useMutation({
		mutationFn: () => fetchInsights(agentId, true),
		onSuccess: (data) => {
			setForm(data);
			setRegenCoolingDown(true);
			invalidateAgentProfile();
			toast.success("Insights regenerated");
		},
		onError: (err: Error & { status?: number }) => {
			if (err.status === 429) {
				setRegenCoolingDown(true);
				toast.error(err.message);
			} else {
				toast.error(err.message || "Failed to regenerate insights");
			}
		},
	});

	const saveMutation = useMutation({
		mutationFn: () =>
			saveInsights(agentId, {
				...form,
				categories: Array.from(
					new Set(
						form.categories
							.map((c) => normalizeAgentCategory(c))
							.filter((c): c is AgentCategory => c !== null),
					),
				).slice(0, 3),
			}),
		onSuccess: () => {
			invalidateAgentProfile();
			toast.success("Insights saved");
		},
		onError: () => toast.error("Failed to save insights"),
	});

	return (
		<div className="space-y-4">
			{isLoading ? (
				<div className="space-y-3">
					{INSIGHT_SKELETON_KEYS.map((key) => (
						<div key={key} className="h-9 w-full animate-pulse bg-muted" />
					))}
				</div>
			) : (
				<>
					{fetchError ? (
						<p className="border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
							{fetchError instanceof Error
								? fetchError.message
								: "Could not load AI insights. You can still edit and save them manually."}
						</p>
					) : null}
					<label
						htmlFor={`${formId}-value-proposition`}
						className="block space-y-1.5 text-base"
					>
						<span className="text-foreground">Value Proposition</span>
						<BrandTextarea
							id={`${formId}-value-proposition`}
							value={form.valueProposition}
							onChange={(e) =>
								setForm((f) => ({ ...f, valueProposition: e.target.value }))
							}
							minHeight={80}
							maxHeight={220}
							placeholder="What the agent does and its key benefit…"
						/>
					</label>
					<label
						htmlFor={`${formId}-problem-solved`}
						className="block space-y-1.5 text-base"
					>
						<span className="text-foreground">Problem Solved</span>
						<BrandTextarea
							id={`${formId}-problem-solved`}
							value={form.problemSolved}
							onChange={(e) =>
								setForm((f) => ({ ...f, problemSolved: e.target.value }))
							}
							minHeight={48}
							maxHeight={180}
							placeholder="The problem it solves…"
						/>
					</label>
					<label
						htmlFor={`${formId}-pricing`}
						className="block space-y-1.5 text-base"
					>
						<span className="text-foreground">Pricing</span>
						<BrandTextarea
							id={`${formId}-pricing`}
							value={form.pricing}
							onChange={(e) =>
								setForm((f) => ({ ...f, pricing: e.target.value }))
							}
							minHeight={48}
							maxHeight={180}
							placeholder="Pricing model…"
						/>
					</label>
					<label
						htmlFor={`${formId}-target-audience`}
						className="block space-y-1.5 text-base"
					>
						<span className="text-foreground">Target Audience</span>
						<BrandTextarea
							id={`${formId}-target-audience`}
							value={form.targetAudience}
							onChange={(e) =>
								setForm((f) => ({ ...f, targetAudience: e.target.value }))
							}
							minHeight={48}
							maxHeight={180}
							placeholder="Ideal customer…"
						/>
					</label>
					<fieldset className="block space-y-1.5 text-base">
						<legend className="text-foreground">Market</legend>
						<div className="flex flex-wrap gap-1.5">
							{AGENT_CATEGORIES.map((cat) => {
								const Icon = CATEGORY_ICONS[cat.value] ?? Building2;
								const selected = form.categories.includes(cat.value);
								const atLimit = form.categories.length >= 3;
								return (
									<button
										key={cat.value}
										type="button"
										onClick={() => toggleCategory(cat.value)}
										disabled={!selected && atLimit}
										aria-pressed={selected}
										className={`inline-flex items-center gap-1.5 border px-2 py-0.5 font-mono text-sm uppercase tracking-[0.12em] transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
											selected
												? "border-brand bg-brand/5 text-brand"
												: "border-border bg-background text-muted-foreground hover:border-brand/40 hover:text-brand"
										}`}
									>
										<Icon className="size-3 shrink-0" />
										{cat.label}
									</button>
								);
							})}
						</div>
						<p className="text-sm text-muted-foreground">
							Pick up to 3 categories
						</p>
					</fieldset>
					<div className="flex items-center gap-2 pt-1">
						<button
							type="button"
							onClick={() => saveMutation.mutate()}
							disabled={saveMutation.isPending}
							className="inline-flex min-h-10 items-center gap-2 bg-brand px-4 font-mono text-sm uppercase tracking-[0.14em] text-background transition-[background-color,transform] hover:bg-brand-hover active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-40"
						>
							{saveMutation.isPending ? "Saving" : "Save"}
						</button>
						<button
							type="button"
							onClick={() => regenerateMutation.mutate()}
							disabled={regenerateMutation.isPending || regenCoolingDown}
							className="inline-flex min-h-10 items-center gap-2 border border-border bg-background px-4 font-mono text-sm uppercase tracking-[0.14em] text-foreground transition-[color,border-color,transform] hover:border-brand/40 hover:text-brand active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-40"
							title={
								regenCoolingDown
									? "Regeneration is limited to once per day"
									: undefined
							}
						>
							<Lightbulb className="size-3.5" />
							{regenerateMutation.isPending
								? "Generating"
								: regenCoolingDown
									? "Available tomorrow"
									: "Regenerate"}
						</button>
					</div>
				</>
			)}
		</div>
	);
}

export function AgentInsightsRegenerateButton({
	agentId,
	onRegenerated,
}: {
	agentId: string;
	onRegenerated?: () => void;
}) {
	const { mutate, isPending } = useMutation({
		mutationFn: () => fetchInsights(agentId, true),
		onSuccess: () => onRegenerated?.(),
	});

	return (
		<button
			type="button"
			onClick={() => mutate()}
			disabled={isPending}
			className="inline-flex min-h-10 items-center gap-2 border border-border bg-background px-4 font-mono text-sm uppercase tracking-[0.14em] text-foreground transition-[color,border-color,transform] hover:border-brand/40 hover:text-brand active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-40"
		>
			<Lightbulb className="size-3.5" />
			{isPending ? "Generating" : "Regenerate AI insights"}
		</button>
	);
}

function InsightRow({
	icon,
	label,
	value,
}: {
	icon: React.ReactNode;
	label: string;
	value: React.ReactNode;
}) {
	return (
		<div className="flex gap-3">
			<div className="mt-0.5 flex size-7 shrink-0 items-center justify-center border border-border bg-background text-muted-foreground">
				{icon}
			</div>
			<div className="min-w-0">
				<p className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
					&gt; {label}
				</p>
				<div className="mt-1 text-pretty text-base text-foreground">
					{value}
				</div>
			</div>
		</div>
	);
}
